import type {ReferralJob} from 'src/types/referral-v2';
import type {JobResponse} from 'src/types/referral-external';
import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useDispatch, useSelector} from 'react-redux';
import {useAsyncDependency} from 'src/hooks/useAsyncDependency';
import {isMobile} from 'src/utils/responsive';
import {JOB_LIST_DATA_LIMIT} from 'src/utils/referral-v2';
import {isSubstringPresent} from 'src/components/referral-v2/common';
import {getProgramJobs} from 'src/action-creators/referrer-portal/user-form';
import {selectUserAgent} from 'src/selectors/accounts';
import {selectJobs} from 'src/selectors/referrer-portal';
import {
  type MenuOption,
  SearchInput,
  ButtonDropdown,
  CircularLoader,
  EmptyState,
} from '@spaced-out/ui-design-system/lib/components/index';
import {
  JobCard,
  MobileViewJobCard,
} from 'src/components/referral-external/jobs/job-card';
import css from 'src/components/referral-external/jobs/jobs.css';


const JobList = ({
  setClickedJobId,
  setJobDetailsOpen,
  handleAssignJob,
  programId,
  agencyId,
}: {
  setClickedJobId: (jobId: string) => unknown;
  setJobDetailsOpen: (data: boolean) => unknown;
  handleAssignJob: (data: ReferralJob) => unknown;
  programId: string;
  agencyId: string;
}) => {
  const dispatch = useDispatch();
  const {t} = useLabelTranslation();
  const [searchText, setSearchText] = React.useState('');
  const [selectedLocation, setSelectedLocation] = React.useState('');

  const jobs: JobResponse[] = useSelector(selectJobs) || [];

  const userAgent = useSelector(selectUserAgent);

  const getLocationOptions = (): MenuOption[] => {
    const uniqueLocations = new Set();
    jobs.forEach((job) => {
      if (job.location) {
        uniqueLocations.add(job.location);
      }
    });
    const options = Array.from(uniqueLocations).map((location) => ({
      key: location,
      label: location,
    }));
    // @ts-expect-error - TS2322 - Type '{ key: unknown; label: unknown; }[]' is not assignable to type 'MenuOption[]'.
    return options;
  };

  const filteredJob = React.useMemo(() => {
    const isLocationMatched = (job: JobResponse) => {
      return selectedLocation ? selectedLocation === job.location : true;
    };

    return jobs.filter(
      (job) =>
        job.id &&
        (!searchText || isSubstringPresent(searchText, job.title)) &&
        isLocationMatched(job),
    );
  }, [searchText, selectedLocation, jobs]);

  // location and title filter is getting handled from frontend
  const getJobs = (value = searchText) => {
    const filtersPayload = {
      search_str: value,
      offset: 0,
      limit: JOB_LIST_DATA_LIMIT,
      location: '',
    } as const;

    return dispatch(getProgramJobs(agencyId, programId, filtersPayload));
  };

  const handleChangeLocation = (location: MenuOption) => {
    const updatedLocation =
      selectedLocation === location.key ? '' : location.key;
    setSelectedLocation(updatedLocation);
  };

  const isJobsLoading = useAsyncDependency(() => getJobs(), []);

  return (
    <div className={css.jobsContainer}>
      <div className={css.navBar}>
        <SearchInput
          placeholder={t('Search_by_title', 'Search by title')}
          onChange={(e) => {
            setSearchText(e.target.value);
          }}
          size="medium"
          value={searchText}
          onClear={() => {
            setSearchText('');
          }}
          classNames={{wrapper: css.searchInput}}
        />
        <ButtonDropdown
          isLoading={isJobsLoading}
          classNames={{buttonWrapper: css.dropdownMenu}}
          menu={{
            allowSearch: true,
            isFluid: false,
            options: getLocationOptions(),
            selectedKeys: [selectedLocation],
          }}
          iconLeftName="location-dot"
          onOptionSelect={handleChangeLocation}
          size="medium"
          type="tertiary"
        >
          {t('Location', 'Location')}
        </ButtonDropdown>
      </div>
      {isJobsLoading ? (
        <CircularLoader
          className={css.loadingState}
          colorToken="colorFillPrimary"
          size="medium"
        />
      ) : (
        <div className={css.jobsContainer}>
          {!filteredJob || filteredJob.length === 0 ? (
            <EmptyState
              classNames={{wrapper: css.panelHeight}}
              imageVariant="data"
              title={t('No_Jobs_Available', 'No Jobs Available')}
            />
          ) : (
            filteredJob.map((job) => {
              return isMobile(userAgent) ? (
                <MobileViewJobCard
                  job={job}
                  handleSelectJob={handleAssignJob}
                  selectJob={setClickedJobId}
                  setJobDetailsOpen={setJobDetailsOpen}
                />
              ) : (
                <JobCard
                  job={job}
                  handleSelectJob={handleAssignJob}
                  selectJob={setClickedJobId}
                  setJobDetailsOpen={setJobDetailsOpen}
                />
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default JobList;
